import * as React from 'react';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  PageSection
} from '@patternfly/react-core';
import { SearchIcon } from '@patternfly/react-icons';
import { useNavigate } from 'react-router-dom';

interface NotFoundStateProps {
  /**
   * The record that was looked for, singular and in lower case: 'class', 'course',
   * 'client executive'. Left out for an address that matches no page at all.
   */
  entityLabel?: string;
  /** What the address held that matched nothing - the id, the offering id, the SKU. */
  identifier?: string | number;
  /** How to name that value to a user: 'offering ID', 'SKU', 'id'. */
  identifierLabel?: string;
  /** Replaces the generated heading. */
  titleText?: string;
  /** Replaces the generated explanation. */
  children?: React.ReactNode;
  /**
   * The list this record would have been in, offered as the way back. Every detail page
   * has one; the catch-all route does not, and falls back to the dashboard alone.
   */
  backTo?: { path: string; label: string };
}

/** The subdued line above the heading, holding the status code for whoever needs it. */
const codeStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--pf-t--global--font--size--md)',
  fontWeight: 'var(--pf-t--global--font--weight--body)' as React.CSSProperties['fontWeight'],
  color: 'var(--pf-t--global--text--color--subtle)',
  marginBlockEnd: 'var(--pf-t--global--spacer--sm)'
};

/**
 * What a page shows when the thing the address asks for does not exist: a mistyped id, a link
 * to a class that has since been deleted, a bookmark to a route that no longer exists.
 *
 * One component for both, because to a user they are the same event and deserve the same
 * answer - said in the middle of the page rather than in a toast over an empty form, which is
 * what a detail page used to do: `return null` while a notification faded out, leaving nothing
 * on screen and nowhere to go.
 *
 * Not an error state, and deliberately not styled as one: nothing failed, the record simply is
 * not there. Genuine failures - the service being down, a rejected request - belong in
 * DataLoadError, which says why and offers a retry. A retry here would only ask again for
 * something that does not exist.
 */
const NotFoundState: React.FunctionComponent<NotFoundStateProps> = ({
  entityLabel,
  identifier,
  identifierLabel = 'id',
  titleText,
  children,
  backTo
}) => {
  const navigate = useNavigate();

  const heading = titleText
    ?? (entityLabel
      ? `${entityLabel.charAt(0).toUpperCase()}${entityLabel.slice(1)} not found`
      : 'Page not found');

  const explanation = children
    ?? (entityLabel
      ? (
        <>
          {identifier
            ? <>There is no {entityLabel} with {identifierLabel} <strong>{identifier}</strong>.</>
            : <>There is no such {entityLabel}.</>}
          {' '}It may have been removed, or the address may have a typo in it.
        </>
      )
      : <>We didn&apos;t find a page that matches the address you navigated to.</>);

  return (
    <PageSection hasBodyWrapper={false} isFilled>
      {/* The height is asked for rather than inherited: the layout's main is not a flex column,
          so isFilled alone leaves the section at its content's height and the state sits under
          the breadcrumb instead of in the middle of the page. */}
      <Bullseye style={{ minHeight: '60vh' }}>
        <EmptyState headingLevel="h1" icon={SearchIcon} variant="lg" titleText={
          <>
            <span style={codeStyle}>Error 404</span>
            {heading}
          </>
        }>
          <EmptyStateBody>{explanation}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              {backTo && (
                // A way back to the list, so it carries the same restoreFilters state the
                // layout's Back button does and the list returns filtered as it was left.
                <Button variant="primary" onClick={() => navigate(backTo.path, { state: { restoreFilters: true } })}>
                  {backTo.label}
                </Button>
              )}
              <Button variant={backTo ? 'secondary' : 'primary'} onClick={() => navigate('/')}>
                Take me home
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </Bullseye>
    </PageSection>
  );
};

export { NotFoundState };
